import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import ControlGroup from '@amzn/meridian/control-group';
import DatePicker from '@amzn/meridian/date-picker';
import Divider from '@amzn/meridian/divider';
import { DLS_REFERRAL } from 'src/config/AccommodationStatusNames';
import { getAustinLink } from 'src/utils/helpers';
import {
  getBccEmailList,
  getPlacementDetails,
  getReferralClaimsStatus,
  setIsUpdateStatusModalOpen,
} from 'src/store/placementDetailsPageSlice/placementDetailsPageSlice';
import Input from '@amzn/meridian/input';
import Link from '@amzn/meridian/link';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import { ModalFooter } from '@amzn/meridian/modal';
import PlacementDetailsService, { UpdateStatusDlsReferralPayload } from 'src/services/placementDetailsService';
import RadioButton from '@amzn/meridian/radio-button';
import React, { useEffect, useState } from 'react';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { UpdateStatusProgress } from '../constants';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import useForm from 'src/hooks/useForm';
import { ReferralAndClaimsStatusEnum } from 'src/store/placementDetailsPageSlice/types';

interface DlsReferralFormData extends Omit<UpdateStatusDlsReferralPayload, 'accommodationId' | 'dlsStatus'> {
  referralAndClaimsStatus: ReferralAndClaimsStatusEnum;
}

interface DlsReferralFormEntryProps {
  setProgress: (val: UpdateStatusProgress) => void;
}

const DlsReferralFormEntry = ({ setProgress }: DlsReferralFormEntryProps) => {
  const dispatch = useAppDispatch();

  const {
    bccEmailList,
    placementDetails: { externalCaseId },
    placementId,
  } = useAppSelector((state) => state.placementDetailsPage);

  const [showSpinner, setShowSpinner] = useState(false);
  const { formData, handleChange, validateForm, errors } = useForm<DlsReferralFormData>({
    validations: {
      placementDetails: { required: { value: true, message: 'Placement details is required' } },
      lastDayWorked: { required: { value: true, message: 'Last day worked is required' } },
      firstDayAbsent: { required: { value: true, message: 'First day absent is required' } },
      referralAndClaimsStatus: { required: { value: true, message: 'Please select one' } },
    },
  });

  useEffect(() => {
    dispatch(getBccEmailList({ placementId: placementId, emailType: DLS_REFERRAL }));
  }, []);

  const onSave = async () => {
    if (validateForm()) {
      setShowSpinner(true);
      try {
        // Remove referralAndClaimsStatus property not sent to backend
        const { referralAndClaimsStatus, ...filteredForm } = formData;
        const payload = {
          accommodationId: placementId,
          dlsStatus: getReferralClaimsStatus(referralAndClaimsStatus),
          ...filteredForm,
        };
        const updateStatusResponse = await PlacementDetailsService.updateStatusDlsReferral(payload);
        if (updateStatusResponse?.status === 200) {
          dispatch(getPlacementDetails(placementId));
          dispatch(setIsUpdateStatusModalOpen(false));
        }
      } finally {
        setShowSpinner(false);
      }
    }
  };

  return (
    <>
      <Text id="update-placement-dls-referral-info-text">Enter required information for DLS Referral email</Text>
      <Row widths="fill" spacingInset={'400 none'}>
        <LoadingSpinnerWrapper showSpinner={showSpinner}>
          <Row widths={['fill']} spacingInset={'200 none'}>
            <Column spacing="200">
              <Text tag="label" type="b200" color="primary">
                Email Recipients
              </Text>
              <Input
                value={formData.emailRecipients}
                placeholder="Enter semicolon separated email recipients"
                onChange={(value) => handleChange('emailRecipients', value)}
                data-cy="Email"
              />
            </Column>
          </Row>
          {bccEmailList.length > 0 && (
            <Text>
              This email automatically notifies
              {bccEmailList.map((email, i) => (
                <>
                  {' '}
                  <Link>{email}</Link> {i < bccEmailList.length - 1 ? '&' : ' '}
                </>
              ))}
              when sent. You may add additional email addresses in the recipients field
            </Text>
          )}
          <Row widths={['fill']} spacingInset={'200 none'}>
            <Column spacing="200">
              <Text tag="label" type="b200" color="primary">
                AUSTIN Link
              </Text>
              <Input disabled={true} placeholder={getAustinLink(externalCaseId)} data-cy="AUSTIN" />
            </Column>
          </Row>
          <Row widths={['fill']} spacingInset={'200 none'}>
            <Column spacing="200">
              <Text tag="label" type="b200" color="primary">
                Placement Details
              </Text>
              <Input
                value={formData.placementDetails}
                placeholder="Enter placement details"
                onChange={(value) => handleChange('placementDetails', value)}
                data-cy="Placement"
                required={true}
                {...errors.placementDetails}
              />
            </Column>
          </Row>
          <Row widths={['fill']} spacingInset={'200 none'}>
            <Column spacing="200">
              <Text tag="label" type="b200" color="primary">
                Additional Notes (if applicable)
              </Text>
              <Input
                value={formData.additionalNotes}
                placeholder="Enter additional notes"
                onChange={(value) => handleChange('additionalNotes', value)}
                data-cy="Additional"
              />
            </Column>
          </Row>
          <Row widths={['fill']} spacingInset={'200 none'}>
            <Column spacing="200">
              <Text tag="label" type="b200" color="primary">
                Last Day Worked
              </Text>
              <DatePicker
                placeholder="MM/DD/YYYY"
                value={formData.lastDayWorked}
                onChange={(value) => handleChange('lastDayWorked', value)}
                data-cy="send-email__dateFields--0"
                {...errors.lastDayWorked}
              />
            </Column>
          </Row>
          <Row widths={['fill']} spacingInset={'200 none'}>
            <Column spacing="200">
              <Text tag="label" type="b200" color="primary">
                First Day Absent
              </Text>
              <DatePicker
                placeholder="MM/DD/YYYY"
                value={formData.firstDayAbsent}
                onChange={(value) => handleChange('firstDayAbsent', value)}
                data-cy="send-email__dateFields--1"
                {...errors.firstDayAbsent}
              />
            </Column>
          </Row>
          <Row widths={['fill']} spacingInset={'200 none'}>
            <Column spacing="200">
              <Text tag="label" type="b200" color="primary">
                Date of Permanent Restrictions (if applicable)
              </Text>
              <DatePicker
                placeholder="MM/DD/YYYY"
                value={formData.permanentRestrictionDate}
                onChange={(value) => handleChange('permanentRestrictionDate', value)}
                data-cy="send-email__dateFields--2"
              />
            </Column>
          </Row>
          <Divider spacingAfter={'400'} spacingBefore={'400'} />
          <Column spacing={'200'}>
            <Text>The Associate:</Text>
            <Text color="error">
              {errors.referralAndClaimsStatus?.error && errors.referralAndClaimsStatus.errorMessage}
            </Text>
            <ControlGroup
              legend=""
              name="Referral and Claims Status"
              data-cy="referral-and-claims-status__radio-btn-grp"
              onChange={(newStatus: number) => handleChange('referralAndClaimsStatus', newStatus)}
              value={formData.referralAndClaimsStatus}
            >
              <RadioButton value={ReferralAndClaimsStatusEnum.HasReachedLimit} data-cy="referralAndClaimsStatus__0">
                Has reached the 180-day TLD TWP limit and requires DLS referral
              </RadioButton>
              <RadioButton
                value={ReferralAndClaimsStatusEnum.HasPermanentRestrictions}
                data-cy="referralAndClaimsStatus__1"
              >
                Has permanent restrictions and requires DLS referral
              </RadioButton>
              <RadioButton
                value={ReferralAndClaimsStatusEnum.HasHadWorkRelatedClaimDenied}
                data-cy="referralAndClaimsStatus__2"
              >
                Has had their work-related claim denied and still requires further accommodation
              </RadioButton>
            </ControlGroup>
          </Column>
        </LoadingSpinnerWrapper>
      </Row>
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fill">
          <Button
            id="update-placement-status-cancel-btn"
            data-cy="placement-status__cancel-btn"
            type="secondary"
            size="small"
            disabled={showSpinner}
            onClick={() => setProgress(UpdateStatusProgress.StatusSelection)}
          >
            Back
          </Button>
          <Button
            id="update-placement-status-save-btn-v2"
            data-cy="placement-status__save_radio-btn"
            type="primary"
            size="small"
            disabled={showSpinner}
            onClick={onSave}
          >
            Update Status and Send Email
          </Button>
        </Row>
      </ModalFooter>
    </>
  );
};

export default DlsReferralFormEntry;
